/** @jsxImportSource @emotion/react */
import { Pencil, Trash2 } from 'lucide-react';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { Roles, ROLES_OPTIONS } from 'modules/roles-manager/domain/interfaces/role';
import { STATUS_OPTIONS } from 'modules/roles-manager/domain/interfaces/status';
import { User } from 'modules/roles-manager/domain/interfaces/user';
import { UpdateUserPayload } from 'modules/roles-manager/domain/resources/role-users';
import Header from 'modules/roles-manager/ui/components/header/header';
import RolesManagerForm, { RolesManagerFormValues } from 'modules/roles-manager/ui/components/roles-manager-form/roles-manager-form';
import useCreateUser from 'modules/roles-manager/ui/hooks/use-create-user';
import useDeleteUser from 'modules/roles-manager/ui/hooks/use-delete-user';
import useUpdateUser from 'modules/roles-manager/ui/hooks/use-update-user';
import useUsersList from 'modules/roles-manager/ui/hooks/use-users-list';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getFullName } from 'shared/utils';
import { getNormalizedDate } from 'shared/utils/get-normalized-date';
import { theme } from 'styles/theme/theme';
import type { DivProps } from 'typings';
import { UDSelect } from 'ud-form/components';
import { UDButton, UDText } from 'ud-ui/components';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import EmptyButton from 'ud-ui/components/empty-button';
import Modal from 'ud-ui/components/modal';
import UDPagination from 'ud-ui/components/pagination';
import { UDTable } from 'ud-ui/components/table';
import UserInfo from 'ud-ui/components/user-info/user-info';
import { useMainLayout } from 'ud-ui/layout/main/context';
import * as S from './styles';

interface IProps extends DivProps {}

function RolesManagerPage(props: IProps) {
  const { ...rest } = props;
  const { setHeaderContent } = useMainLayout();
  const { user: currentUser, userTournamentId: tournamentId } = useAuth();

  const { data: users, filters, pages, page, changeFilter, queryState } = useUsersList();

  const [selectedUser, setSelectedUser] = useState<User>();

  const [isOpenForm, setIsOpenForm] = useState(false);
  const openForm = useCallback(() => {
    setIsOpenForm(true);
  }, []);
  const closeForm = useCallback(() => {
    setIsOpenForm(false);
  }, []);

  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const openDelete = useCallback(() => {
    setIsOpenDelete(true);
  }, []);
  const closeDelete = useCallback(() => {
    setIsOpenDelete(false);
  }, []);

  const openCreateModal = useCallback(() => {
    setSelectedUser(undefined);
    openForm();
  }, [openForm]);

  const openEditModal = useCallback(
    (user: User) => {
      setSelectedUser({ ...user });
      openForm();
    },
    [openForm],
  );

  const openDeleteModal = useCallback(
    (user: User) => {
      setSelectedUser(user);
      openDelete();
    },
    [openDelete],
  );

  const createMutation = useCreateUser(tournamentId ?? undefined);
  const updateMutation = useUpdateUser(tournamentId ?? undefined);
  const deleteMutation = useDeleteUser(tournamentId ?? undefined);

  const onCreate = useCallback(
    async (values: RolesManagerFormValues, formikHelpers: any) => {
      try {
        if (selectedUser) {
          await updateMutation.mutateAsync({ ...values, id: selectedUser.id, role: selectedUser.roles[0] });
          toast.success('Данные пользователя сохранены');
        } else {
          await createMutation.mutateAsync({ ...values, role: Roles.admin });
          toast.success('Пользователь создан');
        }
        closeForm();
      } catch (e) {
        toast.error('Не удалось сохранить изменения');
        throw e;
      } finally {
        formikHelpers?.setSubmitting(false);
      }
    },
    [selectedUser, closeForm, createMutation, updateMutation],
  );

  const onDelete = useCallback(async () => {
    if (!selectedUser) return;
    try {
      await deleteMutation.mutateAsync({ id: selectedUser.id });
      toast.success('Пользователь удалён');
      closeDelete();
    } catch (e) {
      toast.error('Не удалось удалить пользователя');
    }
  }, [selectedUser, deleteMutation, closeDelete]);

  const [inlineProcessingId, setInlineProcessingId] = useState<number | undefined>(undefined);
  const updateUserHandler = useCallback(
    async (user?: User, payload?: Partial<Pick<UpdateUserPayload, 'role' | 'isActive'>>) => {
      if (!user) {
        toast.warn('Ну удалось индицировать пользователя, перезагрузите страницу');
        return;
      }

      if (!payload) {
        toast.warn('Нет изменений');
        return;
      }

      const nothingToUpdate =
        (payload.isActive === undefined || payload.isActive === user.isActive) &&
        (payload.role === undefined || payload.role === user.roles[0]);

      if (nothingToUpdate) {
        toast.warn('Нет изменений');
        return;
      }

      try {
        setInlineProcessingId(user.id);
        await updateMutation.mutateAsync({ ...user, role: user.roles[0], ...payload, photo: undefined });
        toast.success('Роль обновлена');
      } catch (e) {
        toast.error('Не удалось обновить роль');
      } finally {
        setInlineProcessingId(undefined);
      }
    },
    [updateMutation],
  );

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory
        title="Управление пользователями"
        createButtonOnClick={openCreateModal}
        createButtonText="Создать пользователя"
      />,
    );
    return () => setHeaderContent(null);
  }, [openCreateModal, setHeaderContent]);

  return (
    <S.MainBlock {...rest}>
      <Header
        className="mb-5"
        perPage={filters.perPage}
        search={filters.name}
        status={filters.status === null ? null : Boolean(filters.status)}
        phone={filters.phone}
        onChangePerPage={(value: number) => changeFilter('perPage', value)}
        onChangeSearch={(value) => changeFilter('name', value)}
        onChangeStatus={(value) => changeFilter('status', value)}
        onChangePhone={(value) => changeFilter('phone', value)}
      />

      <div>
        <UDTable isLoading={queryState.isLoading || queryState.isFetching}>
          <thead>
            <tr>
              <th className="w-[300px]">ФИО / Время регистрации</th>
              <th className="w-[200px] max-w-[200px] min-w-[106px] max-[1480px]:w-full">Почта</th>
              <th className="w-full min-w-[104px]">Телефон</th>
              <th className="w-[180px] min-w-[180px]">Статус</th>
              <th className="w-[180px] min-w-[180px]">Роль</th>
              <th className="w-[36px] min-w-[36px]" />
              <th className="w-[36px] min-w-[36px]" />
            </tr>
          </thead>
          <tbody>
            {users?.map((user) => {
              const isCurrentUser = user.phone === currentUser?.phone;

              return (
                <tr key={user.id}>
                  <td>
                    <UserInfo
                      avatar={user.photo}
                      title={getFullName(user)}
                      subtitle={user.createdAt && getNormalizedDate(user.createdAt)}
                    />
                  </td>
                  <td>
                    <div className="max-w-full truncate">{user.email ?? '-'}</div>
                  </td>
                  <td>
                    <div className="max-w-full truncate">{user.phone ?? '-'}</div>
                  </td>
                  <td>
                    <UDSelect
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find((o) => user.isActive === o.value) ?? STATUS_OPTIONS[0]}
                      isDisabled={inlineProcessingId === user.id || isCurrentUser}
                      isLoading={inlineProcessingId === user.id || updateMutation.isLoading}
                      onChange={async (opt) => {
                        await updateUserHandler(user, { isActive: opt?.value ?? undefined });
                      }}
                    />
                  </td>
                  <td>
                    <UDSelect
                      options={ROLES_OPTIONS}
                      value={ROLES_OPTIONS.find((o) => user.roles.find((rowRole) => o.value === rowRole)) || ROLES_OPTIONS[0]}
                      isDisabled={inlineProcessingId === user.id || isCurrentUser}
                      isLoading={inlineProcessingId === user.id || updateMutation.isLoading}
                      onChange={async (opt) => {
                        await updateUserHandler(user, { role: opt?.value ?? undefined });
                      }}
                    />
                  </td>
                  <td>
                    <EmptyButton
                      icon={<Pencil size={20} />}
                      className="w-full"
                      onClick={() => openEditModal(user)}
                      disabled={isCurrentUser}
                    />
                  </td>
                  <td>
                    <EmptyButton
                      title="В разработке"
                      disabled={isCurrentUser}
                      color={theme.colors.CaseError}
                      icon={<Trash2 size={20} />}
                      className="w-full"
                      onClick={() => openDeleteModal(user)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </UDTable>
      </div>

      <div className="my-4 flex">
        <UDPagination
          pages={pages}
          currentPage={page}
          onPageChange={(value: number) => {
            changeFilter('page', value);
          }}
        />
      </div>

      <Modal show={isOpenForm} onClose={closeForm}>
        <UDText isNew type="title-1" className="mb-7.5">
          Создание пользователя
        </UDText>
        <RolesManagerForm initValue={selectedUser ?? undefined} onSubmit={onCreate} />
      </Modal>

      <Modal show={isOpenDelete} onClose={closeDelete}>
        <Modal.Header title="Удалить пользователя?" />

        <Modal.Body>
          <UDText isNew type="body-3">
            Вы уверены что хотите удалить доступ для:
          </UDText>
          <UDText isNew type="body-1">
            {getFullName(selectedUser)}
          </UDText>
        </Modal.Body>

        <Modal.Footer justify="end">
          <UDButton variant="secondary" onClick={closeDelete} disabled={deleteMutation.isLoading} stretch>
            Отмена
          </UDButton>
          <UDButton variant="primary" onClick={onDelete} loading={deleteMutation.isLoading} stretch>
            Удалить
          </UDButton>
        </Modal.Footer>
      </Modal>
    </S.MainBlock>
  );
}

export default RolesManagerPage;
